import React, {useCallback, useRef} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteOwnerState, AutocompleteRenderOptionState, Box, TextFieldProps, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {CancelToken} from "axios";

/**
 * Interface representing a customer member in the autocomplete dropdown
 * Defines the structure of each member option with customer association
 *
 * @property id - Unique identifier for the member
 * @property firstName - Member's first name
 * @property lastName - Member's last name
 * @property customerId - Optional ID of the associated customer
 * @property customerName - Optional name of the associated customer
 * @property city - Optional city of the associated customer
 */
interface CustomerMemberOption {
    id: string;
    firstName: string;
    lastName: string;
    customerId?: string;
    customerName?: string;
    city?: string;
}

/**
 * Props interface for the CustomerMemberAutocomplete component
 * Extends the base autocomplete functionality with customer member-specific features
 *
 * @property customerId - Optional customer ID to filter members by customer
 * @property textFieldProps - Additional props to pass to the TextField component
 * @property form - Whether the component is used within a form
 * @property name - Name attribute for form integration
 * @property required - Whether the field is required in forms
 * @property value - Currently selected member(s)
 * @property multiple - Whether multiple members can be selected
 * @property renderOption - Custom render function for dropdown options
 */
interface CustomerMemberAutocompleteProps {
    customerId?: string;
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    value?: CustomerMemberOption | CustomerMemberOption[] | null;
    multiple?: boolean;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement> & { key: any },
        option: CustomerMemberOption,
        state: AutocompleteRenderOptionState,
        ownerState: AutocompleteOwnerState<CustomerMemberOption, false, false, false>
    ) => React.ReactNode;

    [key: string]: any;
}

/**
 * Interface for the customer information API response
 * Contains basic customer details
 *
 * @property data - Customer information container
 * @property data.id - Customer ID
 * @property data.name - Customer name
 * @property data.code - Customer code
 * @property data.city - Customer city
 */
interface CustomerInformationResponse {
    data: {
        id: string;
        name: string;
        code: string;
        city: string;
    };
}

/**
 * Interface for the customer member API response
 * Contains list of members for a specific customer
 *
 * @property data - Array of customer member options
 * @property resultFlag - Indicates if the request was successful
 */
interface CustomerMemberResponse {
    data?: CustomerMemberOption[];
    resultFlag?: boolean;
}

/**
 * Interface for the fast user search API response
 * Contains list of users with their customer associations
 *
 * @property data - Array of user results with customer details
 * @property resultFlag - Indicates if the search was successful
 */
interface FastSearchUsersResponse {
    data?: Array<{
        id: string;
        firstName: string;
        lastName: string;
        customer?: {
            id: string;
            name: string;
            city: string;
        };
    }>;
    resultFlag?: boolean;
}

/**
 * CustomerMemberAutocomplete Component
 *
 * A searchable dropdown component for selecting customer members with the following features:
 * - Two operation modes:
 *   1. Customer-specific: Shows members of a specific customer
 *   2. Global search: Searches across all customers' members
 * - Single or multiple member selection
 * - Custom option rendering with member and customer details
 * - Async option loading with debouncing
 * - Form integration support
 * - Maintains selected values in options list
 *
 * The component handles two distinct data fetching scenarios:
 * 1. With customerId: Fetches members for specific customer
 * 2. Without customerId: Performs global user search
 *
 * @component
 */
const CustomerMemberAutocomplete: React.FC<CustomerMemberAutocompleteProps> = ({
                                                                                   customerId,
                                                                                   textFieldProps = {},
                                                                                   form = false,
                                                                                   name = undefined,
                                                                                   required = false,
                                                                                   value,
                                                                                   multiple,
                                                                                   renderOption: customRenderOption,
                                                                                   ...props
                                                                               }) => {
    const isMounted = useIsMounted();
    const valueRef = useRef(value);
    valueRef.current = value;

    /**
     * Fetches customer member options based on context
     * Handles two scenarios:
     * 1. Customer-specific: Fetches all members for the customer
     * 2. Global search: Searches all users with customer details
     *
     * Always maintains currently selected values in the options list
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string (used only in global search)
     * @returns Array of matching customer member options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        if (customerId) {
            try {
                // Fetch customer details and members
                // @ts-ignore - API type definition is not complete
                const customerInformationResponse = await Api.getCustomerFullInformation(customerId) as CustomerInformationResponse;
                // @ts-ignore - API type definition is not complete
                const response = await Api.getCustomerMembersWithAccessLevels(customerId) as CustomerMemberResponse;
                if (!isMounted())
                    return [];

                // Initialize options with currently selected values
                const options: CustomerMemberOption[] = [];
                if (multiple && Array.isArray(valueRef.current)) {
                    options.push(...valueRef.current);
                } else if (valueRef.current && !multiple) {
                    options.push(valueRef.current as CustomerMemberOption);
                }

                if (!response?.resultFlag) {
                    return options;
                }

                // Add new options, avoiding duplicates
                for (const option of response?.data ?? []) {
                    if (options.find(e => e.id === option.id))
                        continue;
                    options.push({
                        id: option.id,
                        firstName: option.firstName,
                        lastName: option.lastName,
                        customerId: customerId,
                        customerName: customerInformationResponse?.data?.name,
                        city: customerInformationResponse?.data?.city,
                    });
                }
                return options;
            } catch (error) {
                console.error('Error fetching customer members:', error);
                return [];
            }
        } else {
            try {
                // Perform global user search
                // @ts-ignore - API type definition is not complete
                const response = await Api.fastSearchUsers(keywords, cancelToken) as FastSearchUsersResponse;

                // Initialize options with currently selected values
                const options: CustomerMemberOption[] = [];
                if (multiple && Array.isArray(valueRef.current)) {
                    options.push(...valueRef.current);
                } else if (valueRef.current && !multiple) {
                    options.push(valueRef.current as CustomerMemberOption);
                }

                if (!isMounted() || !response?.resultFlag)
                    return options;

                // Add new options, avoiding duplicates
                for (const option of response?.data ?? []) {
                    if (options.find(e => e.id === option.id))
                        continue;
                    options.push({
                        id: option.id,
                        firstName: option.firstName,
                        lastName: option.lastName,
                        customerId: option.customer?.id,
                        customerName: option.customer?.name,
                        city: option.customer?.city,
                    });
                }
                return options;
            } catch (error) {
                console.error('Error searching users:', error);
                return [];
            }
        }
    }, [isMounted, customerId, multiple]);

    /**
     * Determines if two member options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: CustomerMemberOption, value: CustomerMemberOption | null) =>
        option.id === value?.id, []);

    /**
     * Gets the display label for a member option
     * Combines first name, last name, and customer name
     */
    const getOptionLabel = useCallback((option: CustomerMemberOption) =>
        `${option?.firstName ?? ""} ${option?.lastName ?? ""} (${option?.customerName ?? ""})`, []);

    /**
     * Gets the unique key for a member option
     */
    const getOptionKey = useCallback((option: CustomerMemberOption) => option?.id ?? "", []);

    /**
     * Default render function for member options
     * Displays a two-column layout with member details
     *
     * @param props - HTML attributes for the list item
     * @param option - Member option to render
     */
    const defaultRenderOption = useCallback((
        props: React.HTMLAttributes<HTMLLIElement> & { key: any },
        option: CustomerMemberOption
    ) => {
        const {key: _, ...otherProps} = props;
        return (
            <Box component="li" {...otherProps} sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                {/* Header row with column labels */}
                <Grid2 container alignItems={'center'} width={'100%'}>
                    <Grid2 xs={7}>
                        <Typography variant="caption" color="text.secondary">
                            Name
                        </Typography>
                    </Grid2>
                    <Grid2 xs={5}>
                        <Typography variant="caption" color="text.secondary">
                            City
                        </Typography>
                    </Grid2>
                </Grid2>
                {/* Data row with member information */}
                <Grid2 container alignItems={'center'} width={'100%'}>
                    <Grid2 xs={7}>
                        <Typography variant="body2" color="secondary.main">
                            {option.firstName ?? ""} {option.lastName ?? ""} ({option.customerName ?? ""})
                        </Typography>
                    </Grid2>
                    <Grid2 xs={5}>
                        <Typography variant="body2" color="secondary.main">
                            {option.city ?? ''}
                        </Typography>
                    </Grid2>
                </Grid2>
            </Box>
        );
    }, []);

    /**
     * Render function that combines custom and default option rendering
     * Allows for customization while maintaining default layout as fallback
     */
    const renderOption = useCallback((
        props: React.HTMLAttributes<HTMLLIElement> & { key: any },
        option: CustomerMemberOption,
        state: AutocompleteRenderOptionState,
        ownerState: AutocompleteOwnerState<CustomerMemberOption, false, false, false>
    ) => {
        if (customRenderOption) {
            return customRenderOption(props, option, state, ownerState);
        }
        return defaultRenderOption(props, option);
    }, [customRenderOption, defaultRenderOption]);

    return (
        <AutocompleteBase<CustomerMemberOption>
            keywordsBased={!customerId}
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            renderOption={renderOption}
            options={[]} // Initial empty array, will be populated by getOptions
            {...(!form) && {
                value: value as CustomerMemberOption | null,
            }}
            {...props}
        />
    );
};

export default CustomerMemberAutocomplete; 